import { ReferralFullInfo } from "@/modules/user/types/network";
import { create } from "zustand";
import {
  getReferralTree,
  GetReferralTreeParams,
} from "../api/referral/network";
import { logger } from "@/shared/utils/logger";

const LIMIT = 10;

interface NetworkStore {
  path: string;
  filter: string;
  tree: ReferralFullInfo[];
  filtered: ReferralFullInfo[];
  isLoading: boolean;
  hasMoreMap: Record<string, boolean>;
  setToTree: (params: GetReferralTreeParams) => Promise<void>;
  updatePath: (path: string) => void;
  backPath: () => void;
  setFilter: (filter: string) => void;
  dropFiltered: () => void;
  setIsLoading: (isLoading: boolean) => void;
}

export const useNetworkStore = create<NetworkStore>((set, get) => ({
  path: "",
  filter: "",
  tree: [],
  filtered: [],
  isLoading: false,
  hasMoreMap: {
    "": true,
  },
  setToTree: async (params: GetReferralTreeParams) => {
    const { isLoading, hasMoreMap } = get();
    const path = params.path || "";

    if (isLoading || hasMoreMap[path] === false) return;

    set({ isLoading: true });

    const data = await getReferralTree({
      ...params,
      limit: LIMIT,
    });

    if (!data) {
      set({ isLoading: false });
      return;
    }

    const { tree } = get();

    if (data.users.length === 0 && tree.length === 0 && !path) {
      set({
        isLoading: false,
        hasMoreMap: {
          ...hasMoreMap,
          [path]: data.users.length > 0,
        },
      });

      return;
    }

    if (params.filter) {
      set({ filtered: data.users, isLoading: false });

      return;
    }

    if (!path) {
      set({
        tree: [...tree, ...data.users],
        isLoading: false,
        hasMoreMap: {
          ...hasMoreMap,
          [path]: data.users.length > 0,
        },
      });

      return;
    }

    try {
      const pathArr = path
        .toString()
        .split(",")
        .map((i) => i.trim().toUpperCase());

      let currentLevel = tree;

      for (let i = 0; i < pathArr.length; i++) {
        const refCode = pathArr[i];

        const node = currentLevel.find(
          (r) => r.refCode.toUpperCase() === refCode
        );

        if (!node) {
          logger.warn("Path element not found:", refCode);

          set({ isLoading: false });

          return;
        }

        currentLevel = node.children || [];

        if (i === pathArr.length - 1) {
          if (!node.children) {
            node.children = data.users;
          } else if (node.children.length > 0) {
            node.children = [...node.children, ...data.users];
          }
        }
      }

      set({
        tree: [...tree],
        hasMoreMap: { ...hasMoreMap, [path]: data.users.length > 0 },
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      logger.error("Error setting tree:", error);
    }
  },
  updatePath: (refCode: string) => {
    const { path } = get();

    const pathArr = path
      .split(",")
      .map((i) => i.trim())
      .filter((i) => i !== "");

    if (pathArr.length >= 9) return;

    const newRefCode = refCode.trim().toUpperCase();

    if (pathArr.includes(newRefCode)) return;

    pathArr.push(newRefCode);

    set({ path: pathArr.join(",") });
  },
  backPath: () => {
    const { path } = get();

    set({ path: path.split(",").slice(0, -1).join(",") });
  },
  setFilter: (filter: string) => set({ filter }),
  dropFiltered: () => set({ filtered: [] }),
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
}));
